import { useApiToast } from "components/Toaster";
import { getDatalists } from "features/datalist";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useDeleteProfileDocumentMutation } from "services/document/document.endpoint";
import { Document } from "services/document/document.types";
import { useAddDocumentMutation, useGetPersonManagersMutation, useUpdateResponsiblePersonMutation } from "services/person/person.endpoint";
import { Person } from "services/person/person.types";
import { User } from "types/User";

export function useHelpedPersonVM(person: Person, setPerson: (person: Person) => void) {
    const { datalists } = useSelector(getDatalists);
    const { showErrorToast, showApiToast } = useApiToast();
    const [personManagersOptions, setPersonManagersOptions] = useState<{label: string;value: number|null;}[]>([]);
    let { personId } = useParams();


    const navigate = useNavigate();

    const [addDocument, {
        isLoading: isAddDocumentLoading,
        isError: isAddDocumentError,
        isSuccess: isAddDocumentSuccess,
        error: addDocumentError,
        data: addDocumentResponse
    }] = useAddDocumentMutation();

    const [deleteProfileDocument, {
        isLoading: isDeleteProfileDocumentLoading,
        isError: isDeleteProfileDocumentError,
        isSuccess: isDeleteProfileDocumentSuccess,
        error: deleteProfileDocumentError,
        data: deleteProfileDocumentResponse
    }] = useDeleteProfileDocumentMutation();

    const [getPersonManagers, {
        isLoading: isGetPersonManagersLoading,
        isError: isGetPersonManagersError,
        isSuccess: isGetPersonManagersSuccess,
        error: getPersonManagersError,
        data: getPersonManagersResponse
    }] = useGetPersonManagersMutation();

    const [updateResponsiblePerson, {
        isLoading: isUpdateResponsiblePersonLoading,
        isError: isUpdateResponsiblePersonError,
        isSuccess: isUpdateResponsiblePersonSuccess,
        error: updateResponsiblePersonError,
        data: updateResponsiblePersonResponse
    }] = useUpdateResponsiblePersonMutation();

    useEffect(() => {
        if(personId) {
            getPersonManagers({ id: parseInt(personId) })
        }
    }, [])

    useEffect(() => {
        if (isAddDocumentLoading) return;
        if (isAddDocumentError && addDocumentError) {
            showErrorToast(addDocumentError, {
                message: `Erreur lors de l'ajout du document au profil`
            });
        }
        if (isAddDocumentSuccess && addDocumentResponse) {
            showApiToast(addDocumentResponse, {
                successMessage: `Document ajouté au profil avec succès`,
                errorMessage: `Erreur lors de l'ajout du document`
            });
            return;
        }
    }, [isAddDocumentLoading, isAddDocumentError, isAddDocumentSuccess, addDocumentError, addDocumentResponse, showErrorToast, showApiToast]);

    useEffect(() => {
        if (isGetPersonManagersLoading) return;
        if (isGetPersonManagersError && getPersonManagersError) {
            showErrorToast(addDocumentError, {
                message: `Erreur lors de la récupération des appv`
            });
        }
        if (isGetPersonManagersSuccess && getPersonManagersResponse) {
            const managers = getPersonManagersResponse.data.map((manager: User) => {
                return {
                    value: manager.id,
                    label: manager.fullname || ""
                }
            });
            setPersonManagersOptions([{
                value: null,
                label: "Aucun appv"
            }, ...managers])
            return;
        }
    }, [isGetPersonManagersLoading, isGetPersonManagersError, isGetPersonManagersSuccess, getPersonManagersError, getPersonManagersResponse, showErrorToast, showApiToast]);

    useEffect(() => {
        if (isUpdateResponsiblePersonLoading) return;
        if (isUpdateResponsiblePersonError && updateResponsiblePersonError) {
            showErrorToast(addDocumentError, {
                message: `Erreur lors du changement de l'appv`
            });
        }
        if (isUpdateResponsiblePersonSuccess && updateResponsiblePersonResponse) {
            showApiToast(updateResponsiblePersonResponse, {
                successMessage: `Appv modifié avec succès`
            })
            return;
        }
    }, [isUpdateResponsiblePersonLoading, isUpdateResponsiblePersonError, isUpdateResponsiblePersonSuccess, updateResponsiblePersonError, updateResponsiblePersonResponse, showErrorToast, showApiToast]);

    useEffect(() => {
        if (isDeleteProfileDocumentLoading) return;
        if (isDeleteProfileDocumentError && deleteProfileDocumentError) {
            showErrorToast(addDocumentError, {
                message: `Erreur lors de la suppression du document`
            });
        }
        if (isDeleteProfileDocumentSuccess && deleteProfileDocumentResponse) {
            showApiToast(deleteProfileDocumentResponse, {
                successMessage: `Document supprimé au profil avec succès`,
                errorMessage: `Erreur lors de la suppression du document`
            });
            return;
        }
    }, [isDeleteProfileDocumentLoading, isDeleteProfileDocumentError, isDeleteProfileDocumentSuccess, deleteProfileDocumentError, deleteProfileDocumentResponse, showErrorToast, addDocumentError, showApiToast]);

    function handleUploadDocument(document: Document) {
        if (!person) return;
        //add document to person
        const updatedPerson = { ...person, documents: [...person.documents, document] };
        setPerson(updatedPerson);

        addDocument({
            id: person?.id,
            body: document
        })
    }

    function handleUpdateDocument(document: Document) {
        if (!person) return;
        const updatedPerson = { ...person, documents: person.documents.map(d => d.id === document.id ? document : d) };
        setPerson(updatedPerson);
        addDocument({
            id: person?.id,
            body: document
        })
    }

    function handleDeleteDocument(document: Document) {
        if (!person) return;
        const updatedPerson = { ...person, documents: person.documents.filter(d => d.id !== document.id) };
        setPerson(updatedPerson);
        deleteProfileDocument({
            personId: person?.id,
            docId: document.id
        })
    }


    function handleUpdateResponsiblePerson(val: any) {
        if(personId) {
            updateResponsiblePerson({
                id: parseInt(personId),
                body: {
                    id: val.value
                }
            })
        }
    }

    return {
        navigate, 
        datalists,
        personManagersOptions,
        handleUploadDocument,
        handleUpdateDocument,
        handleDeleteDocument,
        handleUpdateResponsiblePerson
    }
}
